import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, PrimaryButton, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import css from './FAQPage.module.css';

//Static assets
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import FAQSection from './FAQSection';
import SectionNewsletter from './SectionNewsletter';
import { fetchAllFaqs, getFaqsSelector } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';

export const FAQPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();

  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'FAQPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({ id: 'FAQPage.schemaDescription' });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  useEffect(() => {
    dispatch(fetchAllFaqs({}));
  }, [dispatch]);

  const faqsData = useSelector(getFaqsSelector) || [];

  // Build FAQPage JSON-LD from the live Strapi FAQ data so Google can render
  // the Q&A rich-result. Strip HTML from answers (schema wants plain text).
  const stripHtml = html =>
    (html || '')
      .replace(/<[^>]*>/g, ' ')
      // named entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&#x27;|&#39;|&rsquo;|&lsquo;|&apos;/g, "'")
      .replace(/&quot;|&ldquo;|&rdquo;/g, '"')
      .replace(/&mdash;/g, '\u2014')
      .replace(/&ndash;/g, '\u2013')
      .replace(/&hellip;/g, '\u2026')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      // numeric entities (decimal + hex)
      .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(parseInt(n, 10)))
      .replace(/&#x([0-9a-fA-F]+);/g, (_, n) => String.fromCodePoint(parseInt(n, 16)))
      // &amp; LAST so we don't double-decode (e.g. &amp;lt; -> &lt; -> <)
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();

  const faqEntities = (faqsData || [])
    .map(faq => {
      const question = faq?.question || faq?.attributes?.question;
      const answerRaw =
        faq?.htmlDescription ||
        faq?.attributes?.htmlDescription ||
        faq?.answer ||
        faq?.attributes?.answer;
      const answer = stripHtml(answerRaw);
      if (!question || !answer) return null;
      return {
        '@type': 'Question',
        name: stripHtml(question),
        acceptedAnswer: { '@type': 'Answer', text: answer },
      };
    })
    .filter(Boolean);

  const webPageSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    description: schemaDescription,
    name: schemaTitle,
    image: [schemaImage],
  };

  const faqSchema =
    faqEntities.length > 0
      ? {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqEntities,
        }
      : null;

  const pageSchema = faqSchema ? [webPageSchema, faqSchema] : webPageSchema;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={pageSchema}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <div className={css.faqHead}>
            <span>{intl.formatMessage({ id: 'FAQPage.faqLabel' })}</span>
            <h1>{intl.formatMessage({ id: 'FAQPage.helpTitle' })}</h1>
            <p>{intl.formatMessage({ id: 'FAQPage.helpDescription' })}</p>
          </div>
          {isArrayLength(faqsData) ? (
            <FAQSection faqsData={faqsData} />
          ) : (
            <div className={css.iconSpinner}>
              <IconSpinner />
            </div>
          )}
          <SectionNewsletter />
          {/* <SectionGetInTouch /> */}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

FAQPageComponent.propTypes = {
  scrollingDisabled: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
};

FAQPageComponent.defaultProps = {};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const FAQPage = compose(
  withRouter,
  connect(mapStateToProps, null),
  withViewport,
  injectIntl
)(FAQPageComponent);

export default FAQPage;
